import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "existingSelect", "newNameInput",
    "existingPanel", "newPanel", "existingTab", "newTab",
    "contextFields", "existingSummary",
    "summaryGoldenMetric", "summaryPrimaryOutcome",
    "editGoldenMetric", "editPrimaryOutcome",
    "newGoldenMetric", "newPrimaryOutcome"
  ]

  static values = {
    seriesData: { type: Array, default: [] }
  }

  connect() {
    this.#togglePanels()
    if (this.hasExistingSelectTarget && this.existingSelectTarget.value) {
      this.#populateExistingSummary()
    }
  }

  switchToExisting() {
    if (this.hasNewNameInputTarget) this.newNameInputTarget.value = ""
    if (this.hasExistingPanelTarget) this.existingPanelTarget.classList.remove("hidden")
    if (this.hasNewPanelTarget) this.newPanelTarget.classList.add("hidden")
    this.#styleTab("existing")
    this.#togglePanels()
  }

  switchToNew() {
    if (this.hasExistingSelectTarget) this.existingSelectTarget.value = ""
    if (this.hasNewPanelTarget) this.newPanelTarget.classList.remove("hidden")
    if (this.hasExistingPanelTarget) this.existingPanelTarget.classList.add("hidden")
    this.#styleTab("new")
    if (this.hasNewNameInputTarget) this.newNameInputTarget.focus()
    this.#togglePanels()
  }

  selectExisting() {
    if (this.hasNewNameInputTarget && this.existingSelectTarget.value) {
      this.newNameInputTarget.value = ""
    }
    this.#togglePanels()
    this.#populateExistingSummary()
  }

  typeNew() {
    if (this.hasExistingSelectTarget && this.newNameInputTarget.value) {
      this.existingSelectTarget.value = ""
    }
    this.#togglePanels()
  }

  #togglePanels() {
    const isNewSeries = this.hasNewNameInputTarget && this.newNameInputTarget.value.trim() !== ""
    const isExistingSeries = this.hasExistingSelectTarget && this.existingSelectTarget.value !== ""
    const requiresNewSeries = this.hasNewNameInputTarget && !this.hasExistingSelectTarget

    if (this.hasContextFieldsTarget) {
      const hidden = !(isNewSeries || requiresNewSeries)
      this.contextFieldsTarget.classList.toggle("hidden", hidden)
      // Disable inputs inside hidden panel so they don't submit stale data
      this.#setInputsDisabled(this.contextFieldsTarget, hidden)
    }

    if (this.hasExistingSummaryTarget) {
      const hidden = !isExistingSeries
      this.existingSummaryTarget.classList.toggle("hidden", hidden)
      this.#setInputsDisabled(this.existingSummaryTarget, hidden)
    }

    // Clear values in the hidden panel to avoid stale submissions
    if (!isNewSeries && this.hasContextFieldsTarget) {
      this.#clearInputs(this.contextFieldsTarget)
    }
    if (!isExistingSeries && this.hasExistingSummaryTarget) {
      this.#clearInputs(this.existingSummaryTarget)
    }
  }

  #populateExistingSummary() {
    const selectedId = this.hasExistingSelectTarget ? parseInt(this.existingSelectTarget.value) : null
    if (!selectedId) return

    const series = this.seriesDataValue.find(s => s.id === selectedId)
    if (!series) return

    // Populate summary
    if (this.hasSummaryGoldenMetricTarget) this.summaryGoldenMetricTarget.textContent = series.golden_metric_label || "\u2014"
    if (this.hasSummaryPrimaryOutcomeTarget) this.summaryPrimaryOutcomeTarget.textContent = series.primary_outcome || "\u2014"

    // Populate edit fields
    if (this.hasEditGoldenMetricTarget) this.editGoldenMetricTarget.value = series.golden_metric_label || ""
    if (this.hasEditPrimaryOutcomeTarget) this.editPrimaryOutcomeTarget.value = series.primary_outcome || ""
  }

  #setInputsDisabled(container, disabled) {
    container.querySelectorAll("input, textarea, select").forEach(el => {
      el.disabled = disabled
    })
  }

  #clearInputs(container) {
    container.querySelectorAll("input[type='text'], textarea").forEach(el => {
      el.value = ""
    })
  }

  #styleTab(active) {
    const activeClasses = ["bg-white", "text-gray-900", "shadow-sm"]
    const inactiveClasses = ["text-gray-500", "hover:text-gray-700"]

    if (this.hasExistingTabTarget && this.hasNewTabTarget) {
      const existingTab = this.existingTabTarget
      const newTab = this.newTabTarget

      if (active === "existing") {
        existingTab.classList.add(...activeClasses)
        existingTab.classList.remove(...inactiveClasses)
        newTab.classList.remove(...activeClasses)
        newTab.classList.add(...inactiveClasses)
      } else {
        newTab.classList.add(...activeClasses)
        newTab.classList.remove(...inactiveClasses)
        existingTab.classList.remove(...activeClasses)
        existingTab.classList.add(...inactiveClasses)
      }
    }
  }
}
